import { FC, useMemo, useState } from "react";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Namespace, useT } from "@racoon/i18n";
import { matchesAnySearch } from "@racoon/lib";
import { SearchablePopover, cn, type SearchableSelectOption } from "@racoon/ui";
import { BrandIcPlus, IcCancel } from "@racoon/icons";
import { SortableList } from "~/shared/ui/sortable-list";

type Option = SearchableSelectOption;

type Props = {
  /** Division ids in order; the first is the default for a new job. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** The divisions the zone can hold: the serving provider and its subtree. */
  options: Option[];
  /** Names of stored divisions while the options load. */
  fallbackNames?: Map<string, string>;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
};

/** A division as a "filter tag" chip (Figma 22781:55725); dragging it reorders the zone. */
const ZoneChip: FC<{
  id: string;
  name: string;
  isDefault: boolean;
  disabled?: boolean;
  onRemove: () => void;
}> = ({ id, name, isDefault, disabled, onRemove }) => {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      title={isDefault ? t(Namespace.PickupLocation, "modal.zone.default") : undefined}
      {...attributes}
      {...listeners}
      className={cn(
        "flex h-6 touch-none items-center rounded-full bg-surface-information p-1",
        !disabled && "cursor-grab active:cursor-grabbing",
        isDragging && "relative z-10 shadow",
      )}
    >
      <span className="px-2 text-xs font-medium text-white">{name}</span>
      <button
        type="button"
        aria-label={t(Namespace.PickupLocation, "modal.zone.remove", { name })}
        disabled={disabled}
        onClick={onRemove}
        className="size-4 shrink-0 text-white"
      >
        <IcCancel className="size-full" />
      </button>
    </div>
  );
};

/** The zone's divisions as chips in one box, "+" adds one (Figma 22781:55723). */
export const ResponsibleZoneField: FC<Props> = ({
  value,
  onChange,
  options,
  fallbackNames,
  loading,
  disabled,
  error,
}) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const nameById = new Map(options.map((o) => [o.id, o.name]));
  const addable = useMemo(
    () => options.filter((o) => !value.includes(o.id) && matchesAnySearch([o.name], search)),
    [options, value, search],
  );

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          "flex min-h-[72px] flex-wrap content-start items-center gap-2 rounded-md border border-border-primary bg-white p-2",
          error && "border-text-error",
        )}
      >
        <SortableList
          wrap
          ids={value}
          onReorder={(from, to) => onChange(arrayMove(value, from, to))}
        >
          {value.map((id, index) => (
            <ZoneChip
              key={id}
              id={id}
              name={nameById.get(id) ?? fallbackNames?.get(id) ?? id}
              isDefault={index === 0}
              disabled={disabled}
              onRemove={() => onChange(value.filter((v) => v !== id))}
            />
          ))}
        </SortableList>
        <SearchablePopover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch("");
          }}
          trigger={
            <button
              type="button"
              aria-label={t(Namespace.PickupLocation, "modal.zone.add")}
              disabled={disabled}
              className="size-6 shrink-0 text-icon-action disabled:opacity-40"
            >
              <BrandIcPlus className="size-full" />
            </button>
          }
          searchValue={search}
          onSearchChange={setSearch}
          inputId="container-group-zone-add"
          placeholder={t(Namespace.PickupLocation, "modal.zone.search")}
        >
          {loading ? (
            <div className="p-4 text-center text-sm text-text-subtle">…</div>
          ) : (
            addable.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  onChange([...value, o.id]);
                  setOpen(false);
                  setSearch("");
                }}
                style={{ paddingLeft: 12 + (search ? 0 : (o.depth ?? 0) * 16) }}
                className="w-full truncate px-3 py-2 text-left text-sm text-text-body hover:bg-gray-100"
              >
                {o.name}
              </button>
            ))
          )}
        </SearchablePopover>
      </div>
      {error && <span className="text-xs text-text-error">{error}</span>}
    </div>
  );
};
